import React from "react";
import {
  Link,
  Redirect,
  Route,
  Switch,
  useHistory,
  useLocation,
} from "react-router-dom";
import { api, session } from "./api";
import { Story, Paper, Page, Preview, Session } from "./types";
import { Failure, Loading, useData, useTitle } from "./hooks";
import StoryCard from "./StoryCard";
import EditorDesk from "./EditorDesk";
import AbstractPreview from "./AbstractPreview";
import {
  ArticlePage,
  ArticlePreviewPage,
  BlogHome,
  CategoryPage,
  LeadGroup,
  ProjectArticlesPage,
} from "../blog/pages";
import { BlogHomeData } from "../blog/types";
import { useParams } from "react-router-dom";
import "./editorial.css";
import "../blog/blog.css";

export const INSIDE = "/inside-the-research";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}
function Search({ inside }: { inside: boolean }) {
  const query = useQuery(),
    history = useHistory(),
    [q, setQ] = React.useState(query.get("q") || "");
  React.useEffect(() => setQ(query.get("q") || ""), [query.toString()]);
  return (
    <form
      className="er-search"
      onSubmit={(e) => {
        e.preventDefault();
        const params = new URLSearchParams();
        if (q.trim()) params.set("q", q.trim());
        if (query.get("year")) params.set("year", query.get("year")!);
        history.push((inside ? INSIDE : "/") + "?" + params.toString());
      }}
    >
      <label className="er-sr" htmlFor="er-search">
        {inside ? "Search projects and teams" : "Search preprints and authors"}
      </label>
      <input
        id="er-search"
        type="search"
        placeholder={
          inside
            ? "Search projects, teams or research topics"
            : "Search titles, authors, teams or research questions"
        }
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      <button type="submit">Search</button>
    </form>
  );
}
function YearFilter({ base }: { base: string }) {
  const query = useQuery(),
    history = useHistory(),
    { value } = useData<{ items: { year: number }[] }>("/collections");
  return (
    <label className="er-year">
      Competition year{" "}
      <select
        value={query.get("year") || ""}
        onChange={(e) => {
          const params = new URLSearchParams(query);
          if (e.target.value) params.set("year", e.target.value);
          else params.delete("year");
          history.push(base + "?" + params.toString());
        }}
      >
        <option value="">All years</option>
        {value?.items.map((y) => (
          <option key={y.year} value={y.year}>
            iDEC {y.year}
          </option>
        ))}
      </select>
    </label>
  );
}
// Lead + two supporting project cards; total height of the right column
// matches the lead (see .er-feature-grid). Fewer than three: no empty cell.
export function FeatureGrid({ items }: { items: Story[] }) {
  return (
    <div className="er-feature-grid" data-count={items.length}>
      {items.map((s, i) => (
        <StoryCard key={s.id} story={s} lead={i === 0} compact={i > 0} />
      ))}
    </div>
  );
}
function Highlights() {
  const { value, error } = useData<Page<Story>>("/stories?featured=true&limit=3");
  return (
    <section className="er-featured">
      <div className="er-section-head">
        <div>
          <h2>Research Highlights</h2>
          <p>Discover the questions and experiments behind the preprints.</p>
        </div>
        <Link to={INSIDE}>Explore the research →</Link>
      </div>
      <Failure text={error} />
      {!value && !error ? (
        <Loading />
      ) : value && value.items.length > 0 ? (
        <FeatureGrid items={value.items} />
      ) : (
        <p>Explore the team Wikis to discover the projects.</p>
      )}
    </section>
  );
}
function Commentary() {
  const { value, error } = useData<BlogHomeData>("/blog/home");
  if (error || (value && !value.lead)) return null;
  return (
    <section className="er-featured er-commentary">
      <div className="er-section-head">
        <div>
          <h2>Commentary and Stories</h2>
          <p>Perspectives, voices and insights from the iDEC community.</p>
        </div>
        <Link to="/blog">Explore all articles →</Link>
      </div>
      {!value ? <Loading /> : <LeadGroup lead={value.lead} supporting={value.supporting} />}
    </section>
  );
}
function StoryIndex() {
  const query = useQuery(),
    key = query.toString(),
    { value, error } = useData<Page<Story>>("/stories?" + key + "&limit=12");
  const [pages, setPages] = React.useState<Story[]>([]),
    [moreError, setMoreError] = React.useState(""),
    [busy, setBusy] = React.useState(false);
  const currentKey = React.useRef(key);
  currentKey.current = key;
  React.useEffect(() => {
    setPages([]);
    setMoreError("");
    setBusy(false);
  }, [key]);
  const shown = [...(value?.items || []), ...pages];
  async function more() {
    setBusy(true);
    setMoreError("");
    try {
      const next = await api<Page<Story>>("/stories?" + key + "&limit=12&offset=" + shown.length);
      if (currentKey.current === key) setPages((p) => [...p, ...next.items]);
    } catch (e) {
      if (currentKey.current === key) setMoreError((e as Error).message);
    } finally {
      if (currentKey.current === key) setBusy(false);
    }
  }
  return (
    <section className="er-index">
      <div className="er-section-head">
        <div>
          <div className="er-kicker">Explore the projects</div>
          <h2>All team Wikis</h2>
          <p aria-live="polite">
            {value ? value.total + " public team Wikis" : "Student projects across the competition years"}
            {query.get("q") ? " · Results for “" + query.get("q") + "”" : ""}
          </p>
        </div>
        <YearFilter base={INSIDE} />
      </div>
      <Failure text={error} />
      {!value && !error ? (
        <Loading />
      ) : (
        <div className="er-story-grid">
          {shown.map((s) => (
            <StoryCard key={s.id} story={s} />
          ))}
        </div>
      )}
      {value && shown.length === 0 && (
        <p>
          No matching team Wikis. <Link to={INSIDE}>Clear filters</Link>
        </p>
      )}
      <Failure text={moreError} />
      {value && (
        <div className="er-load">
          <span aria-live="polite">
            Showing {shown.length} of {value.total} team Wikis
          </span>
          {shown.length < value.total && (
            <button disabled={busy} onClick={more}>
              {busy ? "Loading…" : "Show more Wikis ↓"}
            </button>
          )}
        </div>
      )}
    </section>
  );
}
function InsideTheResearch() {
  const q = useQuery();
  useTitle("Inside the Research | idecRxiv");
  return (
    <>
      <div className="er-breadcrumb">
        <Link to="/">Home</Link> / Inside the Research
      </div>
      <div className="er-intro">
        <div className="er-kicker">The iDEC project showcase</div>
        <h1>Inside the Research</h1>
        <p>
          Big questions. Student-led science.
          <br />
          Editors’ Insights, project stories and the team Wikis behind the preprints.
        </p>
      </div>
      {!q.get("q") && !q.get("year") && <Highlights />}
      <StoryIndex />
      <aside className="er-learning">
        <h2>
          Start with a question.
          <br />
          Follow the experiment.
        </h2>
        <p>
          Team Wikis document the scientific question, experimental design and learning process. Related Blog articles offer further routes into a project.
        </p>
        <Link to="/">Browse the preprints →</Link>
      </aside>
    </>
  );
}
function Preprints() {
  const query = useQuery(),
    key = query.toString(),
    [pages, setPages] = React.useState<Paper[]>([]),
    [busy, setBusy] = React.useState(false),
    [moreError, setMoreError] = React.useState(""),
    { value, error } = useData<Page<Paper>>("/articles?" + key + "&limit=5");
  const currentKey = React.useRef(key);
  currentKey.current = key;
  React.useEffect(() => {
    setPages([]);
    setMoreError("");
    setBusy(false);
  }, [key]);
  const shown = [...(value?.items || []), ...pages];
  async function more() {
    setBusy(true);
    setMoreError("");
    try {
      const next = await api<Page<Paper>>("/articles?" + key + "&limit=10&offset=" + shown.length);
      if (currentKey.current === key) setPages((p) => [...p, ...next.items]);
    } catch (e) {
      if (currentKey.current === key) setMoreError((e as Error).message);
    } finally {
      if (currentKey.current === key) setBusy(false);
    }
  }
  return (
    <>
      <div className="er-section-head">
        <div>
          <h1>Latest Preprints</h1>
          <p aria-live="polite">{value?.total ?? "…"} preprints · Newest first</p>
        </div>
        <YearFilter base="/" />
      </div>
      <Failure text={error} />
      {!value && !error && <Loading />}
      {shown.map((p) => (
        <AbstractPreview key={p.articleId} paper={p}>
          <div className="er-kicker">Preprint · iDEC {p.year}</div>
          <h2>
            <a href={p.url}>{p.title}</a>
          </h2>
          <p className="er-deck">
            {p.authors.slice(0, 3).join(", ")}
            {p.authors.length > 3 ? " et al." : ""}
          </p>
          <p className="er-team">
            {p.team} · Posted {p.date}
          </p>
          <div className="er-links">
            <a href={p.url}>Read preprint →</a>
            {p.wikiAvailable && (
              <a href={p.wiki} target="_blank" rel="noopener noreferrer">
                Team Wiki ↗
              </a>
            )}
          </div>
          <details>
            <summary>Abstract & authors</summary>
            <p>{p.abstract}</p>
            <p>{p.authors.join(", ")}</p>
          </details>
        </AbstractPreview>
      ))}
      {value && shown.length === 0 && (
        <p>
          No matching preprints. <Link to="/">Clear filters</Link>
        </p>
      )}
      <Failure text={moreError} />
      {value && (
        <div className="er-load">
          <span aria-live="polite">
            Showing {shown.length} of {value.total} preprints
          </span>
          {shown.length < value.total && (
            <button disabled={busy} onClick={more}>
              {busy ? "Loading…" : "Show more preprints ↓"}
            </button>
          )}
        </div>
      )}
    </>
  );
}
function Home() {
  const query = useQuery(),
    filtered = Boolean(query.get("q") || query.get("year"));
  useTitle("idecRxiv - Research, Inside the Research and Blog");
  return (
    <>
      {!filtered && <Highlights />}
      <div className="er-home-columns">
        <section>
          <Preprints />
        </section>
        <aside>
          <Collections compact />
          <div className="er-side-note">
            <div className="er-kicker">Opinion and stories</div>
            <h2>The Blog</h2>
            <p>Scientific Perspectives, iDEC Voices, Judges’ Perspectives and Industry Insights.</p>
            <Link to="/blog">Explore all articles →</Link>
          </div>
        </aside>
      </div>
      {!filtered && <Commentary />}
    </>
  );
}
function Collections({ compact = false }: { compact?: boolean }) {
  const { value, error } = useData<{ items: { year: number; preprints: number; wikis: number }[] }>("/collections");
  return (
    <section className={compact ? "er-collections compact" : "er-collections"}>
      <div className="er-kicker">The competition archive</div>
      {compact ? <h2>Collections · research by year</h2> : <h1>Collections</h1>}
      {!compact && <p>Each year of iDEC, brought together.</p>}
      <Failure text={error} />
      <div className="er-year-grid">
        {value?.items.map((y) => (
          <article key={y.year}>
            <h2>iDEC {y.year}</h2>
            <p>
              {y.preprints} preprints · {y.wikis} public Wikis
            </p>
            <div className="er-links">
              <Link to={"/?year=" + y.year}>Browse preprints →</Link>
              <Link to={INSIDE + "?year=" + y.year}>Explore Wikis →</Link>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}
function CollectionsPage() {
  useTitle("Collections | idecRxiv");
  return <Collections />;
}
function PreviewPage() {
  // Editors only: the API refuses anonymous readers, and nothing here is
  // written. The draft Insight is rendered by the same components as the
  // public pages so what the editor sees is what publishing will show.
  const { id } = useParams<{ id: string }>(),
    { value, error } = useData<Preview>("/admin/content/" + encodeURIComponent(id) + "/preview");
  useTitle("Draft preview | idecRxiv");
  React.useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex";
    document.head.appendChild(meta);
    return () => meta.remove();
  }, []);
  if (error)
    return (
      <>
        <Failure text={error} />
        <p>
          <Link to="/editor">Go to the editorial desk</Link>
        </p>
      </>
    );
  if (!value) return <Loading />;
  return (
    <>
      <div className="er-preview-banner" role="status">
        <strong>Draft preview · editors only.</strong> This is the saved draft, not the public site. Links open the live pages.{" "}
        <Link to="/editor">Back to the desk</Link>
      </div>
      <section className="er-featured">
        <div className="er-section-head">
          <div>
            <h2>Research Highlights</h2>
            <p>Discover the questions and experiments behind the preprints.</p>
          </div>
          <Link to={INSIDE}>Explore the research →</Link>
        </div>
        {value.inHero ? (
          <FeatureGrid items={value.featured} />
        ) : (
          <p className="er-help">
            Not in this section: tick “Feature on the home page” and give it one of the three lowest display orders to place it here.
            {value.featured.length ? " Currently shown: " + value.featured.map((s) => s.team).join(", ") + "." : ""}
          </p>
        )}
      </section>
      <section className="er-index">
        <div className="er-section-head">
          <div>
            <div className="er-kicker">Explore the projects</div>
            <h2>As a card in All team Wikis</h2>
          </div>
        </div>
        <div className="er-story-grid">
          <StoryCard story={value.story} />
        </div>
      </section>
    </>
  );
}
function LegacyStoriesRedirect() {
  const { search } = useLocation();
  return <Redirect to={INSIDE + search} />;
}
export default function EditorialApp({
  termsPage,
  acknowledgementsPage,
}: {
  termsPage?: React.ReactNode;
  acknowledgementsPage?: React.ReactNode;
} = {}) {
  // The desk link in the masthead is shown to signed-in editors only; readers
  // see a small "Editors" link in the footer. Authorisation stays server-side.
  const [auth, setAuth] = React.useState<Session | null>(null);
  React.useEffect(() => {
    session().then(setAuth).catch(() => setAuth(null));
  }, []);
  const location = useLocation(),
    path = location.pathname,
    inside = path.startsWith(INSIDE) || path.startsWith("/science-stories"),
    blog = path === "/blog" || path.startsWith("/blog/"),
    editor = path === "/editor" || path.startsWith("/preview/") || path.startsWith("/blog/preview/");
  return (
    <div id="idecrxiv-editorial">
      <header>
        <div className="er-utility">
          <span>Directed evolution · Research · Learning</span>
          <a href="https://idec.io/" target="_blank" rel="noopener noreferrer">
            iDEC competition ↗
          </a>
        </div>
        <div className="er-masthead">
          <div>
            <Link to="/" className="er-wordmark">
              idecR<i>χ</i>iv
            </Link>
            <p>Preprints and the stories behind them</p>
          </div>
          {auth?.user && (
            <Link to="/editor" className="er-editor-entry">
              Editorial desk →
            </Link>
          )}
        </div>
        <nav aria-label="Main navigation">
          <Link to="/" aria-current={path === "/" ? "page" : undefined}>
            Research
          </Link>
          <Link to={INSIDE} aria-current={inside ? "page" : undefined}>
            Inside the Research
          </Link>
          <Link to="/blog" aria-current={blog ? "page" : undefined}>
            Blog
          </Link>
          <Link to="/collections" aria-current={path === "/collections" ? "page" : undefined}>
            Collections
          </Link>
          <a href="https://idec.io/">About iDEC ↗</a>
        </nav>
        {!editor && !blog && <Search inside={inside} />}
      </header>
      <main>
        <Switch>
          <Route exact path="/">
            <Home />
          </Route>
          <Route exact path={INSIDE}>
            <InsideTheResearch />
          </Route>
          <Route exact path="/science-stories">
            <LegacyStoriesRedirect />
          </Route>
          <Route exact path="/collections">
            <CollectionsPage />
          </Route>
          <Route exact path="/blog">
            <BlogHome />
          </Route>
          <Route exact path="/blog/category/:key">
            <CategoryPage />
          </Route>
          <Route exact path="/blog/project/:projectId">
            <ProjectArticlesPage />
          </Route>
          <Route exact path="/blog/preview/:id">
            <ArticlePreviewPage />
          </Route>
          <Route exact path="/blog/:slug">
            <ArticlePage />
          </Route>
          <Route exact path="/preview/:id">
            <PreviewPage />
          </Route>
          <Route exact path="/editor">
            <EditorDesk />
          </Route>
          <Route exact path="/terms">
            <section className="er-static">
              <h1>Terms &amp; Conditions</h1>
              {termsPage || <a href="https://arxiv.idec.io/terms">Read the current Terms &amp; Conditions</a>}
            </section>
          </Route>
          <Route exact path="/acknowledgements">
            <section className="er-static">
              <h1>Acknowledgements</h1>
              {acknowledgementsPage || <a href="https://arxiv.idec.io/acknowledgements">Read the current acknowledgements</a>}
            </section>
          </Route>
          <Route>
            <h1>Page not found</h1>
            <Link to="/">Return home</Link>
          </Route>
        </Switch>
      </main>
      <footer>
        <span>idecRχiv · International Directed Evolution Competition</span>
        <div className="er-footer-links">
          <a href="/terms">Terms &amp; Conditions</a>
          <a href="/acknowledgements">Acknowledgements</a>
          <a href="https://reg.idec.io/privacy_policy">Privacy</a>
          <Link to="/editor" className="er-editor-footer">Editors</Link>
        </div>
      </footer>
    </div>
  );
}
